/**
 * Typed client for our own `/api/*` routes.
 *
 * The browser talks only to this app's origin. It never holds a provider key
 * and never names an upstream URL.
 */

import type { Price, PriceHistory, Product } from '@/types/product';
import { canCombineLocally, combineBoards } from '@/lib/combined-ranking';
import { BOARDS } from '@/lib/boards';
import { sliceStatic, type StaticBoardData } from '@/lib/static-boards';

export interface ApiMeta {
  source: string;
  generatedAt: string;
  cached: boolean;
  warnings: string[];
  isSampleData: boolean;
  /**
   * True when this answer came from a file baked at deploy time rather than
   * from the API. The page uses it to describe what a combined ranking covers,
   * since the baked set is narrower than a full catalogue sweep.
   */
  static?: boolean;
}

export interface ApiResult<T> {
  data: T;
  meta: ApiMeta;
  /** True when this response was served by the service worker while offline. */
  fromCache: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Stable per-browser id for tracking. Not an account, and not identifying. */
export function clientId(): string {
  const KEY = 'kcaltrack.clientId';
  let id = localStorage.getItem(KEY);
  if (id === null || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    id = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(KEY, id);
  }
  return id;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: { Accept: 'application/json', ...(init.headers ?? {}) },
    });
  } catch {
    throw new ApiError(
      'offline',
      navigator.onLine
        ? 'Could not reach the server. Please try again.'
        : 'You are offline. Showing cached data where available.',
      0,
    );
  }

  // The service worker adds this when it serves a cached response, which is
  // what lets the UI avoid presenting stale data as live (section 25).
  const fromCache =
    response.headers.get('x-from-sw-cache') === '1' ||
    (response.type === 'basic' && response.headers.get('age') !== null && !navigator.onLine);

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    /*
     * Not JSON. In practice this is the edge returning its own HTML error page,
     * and the status says which kind. Cloudflare's 1102 (Worker over its CPU
     * limit) arrives this way, and calling it "unreadable" told the reader
     * nothing they could act on -- it is a transient overload, and retrying is
     * the right move.
     */
    if (response.status >= 500 || response.status === 0) {
      throw new ApiError(
        'server_busy',
        'The server ran out of time answering that. This usually clears on a retry.',
        response.status,
      );
    }
    throw new ApiError('bad_response', 'The server returned an unreadable response.', response.status);
  }

  const envelope = body as
    | { ok: true; data: T; meta: ApiMeta }
    | { ok: false; error: { code: string; message: string }; meta?: ApiMeta };

  if (!response.ok || envelope.ok === false) {
    const error = 'error' in envelope ? envelope.error : { code: 'error', message: 'Request failed.' };
    const retryAfter = response.headers.get('retry-after');
    throw new ApiError(
      error.code,
      error.message,
      response.status,
      retryAfter === null ? null : Number(retryAfter),
    );
  }

  return { data: envelope.data, meta: envelope.meta, fromCache };
}

// ---------------------------------------------------------------------------

export interface SearchParams {
  query: string;
  limit?: number;
  country?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
}

export interface SearchPayload {
  products: Product[];
  total: number | null;
  providerNotes: string[];
}

export function searchProducts(
  params: SearchParams,
  signal?: AbortSignal,
): Promise<ApiResult<SearchPayload>> {
  const q = new URLSearchParams({ q: params.query });
  if (params.limit) q.set('limit', String(params.limit));
  if (params.country) q.set('country', params.country);
  if (params.category) q.set('category', params.category);
  if (params.minPrice !== undefined) q.set('minPrice', String(params.minPrice));
  if (params.maxPrice !== undefined) q.set('maxPrice', String(params.maxPrice));
  return request<SearchPayload>(`/api/search?${q}`, signal ? { signal } : {});
}

export function getProduct(id: string, signal?: AbortSignal): Promise<ApiResult<Product>> {
  return request<Product>(`/api/product/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export function getPrices(id: string): Promise<ApiResult<{ prices: Price[] }>> {
  return request<{ prices: Price[] }>(`/api/prices/${encodeURIComponent(id)}`);
}

export interface HistoryPayload {
  history: PriceHistory | null;
  origin: 'observed' | 'provider' | 'none';
  requestedDays: number;
  note: string;
}

export function getHistory(id: string, days = 30): Promise<ApiResult<HistoryPayload>> {
  return request<HistoryPayload>(`/api/history/${encodeURIComponent(id)}?days=${days}`);
}

export interface ComparePayload {
  products: Product[];
  failed: Array<{ id: string; reason: string }>;
}

export function compareProducts(
  ids: string[],
  signal?: AbortSignal,
): Promise<ApiResult<ComparePayload>> {
  return request<ComparePayload>(
    `/api/compare?ids=${encodeURIComponent(ids.join(','))}`,
    signal ? { signal } : {},
  );
}

// ---------------------------------------------------------------------------
// Tracking
// ---------------------------------------------------------------------------

export interface TrackedItem {
  id: number;
  productId: string;
  productName: string;
  targetPrice: number | null;
  startPrice: number | null;
  currency: string | null;
  currentPrice: number | null;
  createdAt: string;
}

export interface TrackedPayload {
  tracked: TrackedItem[];
  notificationsEnabled: boolean;
  notificationNote: string;
}

export function listTracked(): Promise<ApiResult<TrackedPayload>> {
  return request<TrackedPayload>('/api/track', { headers: { 'X-Client-Id': clientId() } });
}

export function trackProduct(
  productId: string,
  targetPrice: number | null,
): Promise<ApiResult<{ tracked: boolean; notificationNote: string }>> {
  return request('/api/track', {
    method: 'POST',
    headers: { 'X-Client-Id': clientId(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ productId, targetPrice }),
  });
}

export function untrackProduct(productId: string): Promise<ApiResult<{ tracked: boolean }>> {
  return request(`/api/track?productId=${encodeURIComponent(productId)}`, {
    method: 'DELETE',
    headers: { 'X-Client-Id': clientId() },
  });
}

// ---------------------------------------------------------------------------
// Leaderboards (the landing page)
// ---------------------------------------------------------------------------

export interface BoardDef {
  id: string;
  label: string;
  description: string;
  formula: string;
  needsNutrition: boolean;
}

export interface LeaderboardPayload {
  /** Nutrition coverage, carried here so the page needs one request, not two. */
  coveragePercent?: number | null;
  board: BoardDef;
  boards: BoardDef[];
  products: Product[];
  /** How many catalog products were eligible for this ranking. */
  eligible: number;
  catalogTotal: number;
  category: string | null;
  audience: 'human' | 'pet' | 'all';
  /** Present when several criteria were combined. */
  selected?: BoardDef[];
  combined?: Array<{ productId: string; score: number; criteriaMet: number; criteriaTotal: number; perBoard: Array<{ boardId: string; rank: number; of: number; percentile: number }> }>;
  categories: Array<{ value: string; count: number }>;
  /** Delivery zones we hold prices for. */
  zones?: Array<{ code: string; name: string; address: string | null }>;
  /** The zone applied, or null when showing the cheapest across all. */
  zone?: string | null;
}

export async function getLeaderboard(
  params: {
    board?: string;
    limit?: number;
    category?: string | null;
    audience?: string;
    boards?: string[];
    /**
     * Bumped by the refresh button. Sent as a query param so it defeats the
     * service worker and edge caches too — without it, "refresh" would replay
     * the very response the user is trying to get past.
     */
    refresh?: number;
    /** Rows to skip, for paging past the first screenful. */
    offset?: number;
    /** Delivery zone code, or null for "cheapest across all zones". */
    zone?: string | null;
  },
  signal?: AbortSignal,
): Promise<ApiResult<LeaderboardPayload>> {
  const q = new URLSearchParams();
  if (params.board) q.set('board', params.board);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.category) q.set('category', params.category);
  if (params.audience) q.set('audience', params.audience);
  if (params.boards && params.boards.length > 1) q.set('boards', params.boards.join(','));
  if (params.offset) q.set('offset', String(params.offset));
  if (params.zone) q.set('zone', params.zone);
  if (params.refresh) q.set('_r', String(params.refresh));

  /*
   * The default view comes from a static file, not the API.
   *
   * One criterion, no category, no zone, first page: that is nearly every page
   * view, and the answer is rebuilt once a night. Serving it from a file on
   * Cloudflare Pages costs no Function invocation and no database read, and —
   * the reason this exists — it cannot fail when the database has run out of
   * its daily allowance. That failure took the rankings down for eighteen
   * hours, showing "Nothing to rank" for a catalogue of seventy-six thousand
   * products.
   *
   * Anything else, and any explicit refresh, goes to the API as before.
   */
  const staticEligible =
    (params.refresh === undefined || params.refresh === 0) &&
    !params.category &&
    !params.zone &&
    !params.offset &&
    (params.limit ?? 0) <= STATIC_DEPTH;

  /*
   * A category filter or a later page, from the deep file when it can.
   *
   * This is the expensive request the site could still make — a live scan of
   * roughly 150,000 rows for thirty results. Answering it from an array the
   * device already holds costs nothing, and `sliceStatic` refuses rather than
   * returning a thin list when the file cannot cover the question.
   */
  if (
    (params.refresh === undefined || params.refresh === 0) &&
    !params.zone &&
    (params.boards === undefined || params.boards.length <= 1) &&
    (params.category || params.offset) &&
    (params.offset ?? 0) + (params.limit ?? 30) <= STATIC_DEPTH
  ) {
    const sliced = await sliceFromStatic(
      params.board ?? 'cheapest_per_100g',
      params.audience ?? 'human',
      params.category ?? null,
      params.offset ?? 0,
      params.limit ?? 30,
      signal,
    );
    if (sliced !== null) return sliced;

    /*
     * The catalogue-wide file could not answer this category — the winners for
     * a narrow category sit below its top 240. Try the file baked FOR this
     * category, which holds its own top sixty regardless of where they rank
     * overall. This is the request that would otherwise scan ~70,000 rows live.
     */
    if (params.category && (params.offset ?? 0) + (params.limit ?? 30) <= CATEGORY_DEPTH) {
      const fromCategory = await sliceFromCategoryFile(
        params.board ?? 'cheapest_per_100g',
        params.audience ?? 'human',
        params.category,
        params.offset ?? 0,
        params.limit ?? 30,
        signal,
      );
      if (fromCategory !== null) return fromCategory;
    }
  }

  if (staticEligible && (params.boards === undefined || params.boards.length <= 1)) {
    const fromFile = await readStaticLeaderboard(
      params.board ?? 'cheapest_per_100g',
      params.audience ?? 'human',
      params.limit,
      signal,
    );
    if (fromFile !== null) return fromFile;
  }

  /*
   * Several criteria: combined here, not on the server.
   *
   * This was the site's most expensive endpoint and its most fragile — the one
   * that exceeded the Worker CPU limit and made the page report "Nothing to
   * rank" for a query with ten thousand eligible products. The inputs are
   * already on the device once the baked rankings are cached, and a Borda count
   * over a few hundred rows is microseconds of work in the browser.
   *
   * It combines what the baked rankings contain, which is not the set the
   * server scored; the page says so. If any of the selected rankings is not
   * available locally, this falls through and the server answers as before.
   */
  if (staticEligible && params.boards !== undefined && params.boards.length > 1) {
    const local = await combineLocally(
      params.boards,
      params.audience ?? 'human',
      params.limit ?? 30,
      signal,
    );
    if (local !== null) return local;
  }

  const live = await request<LeaderboardPayload>(`/api/leaderboard?${q}`, {
    ...(signal ? { signal } : {}),
    ...(params.refresh ? { cache: 'reload' as RequestCache } : {}),
  });

  /*
   * A SERVER FAULT IS NOT AN EMPTY RESULT.
   *
   * The same mistake as the 1102 bug, one layer down. When the database ran
   * out of its daily read allowance the endpoint answered 200 with an empty
   * ranking and a catalogue total of zero, and the page rendered "Nothing to
   * rank" — telling the reader their filters matched nothing, for a catalogue
   * of seventy-six thousand products. They then went looking for a filter to
   * change, which could not possibly help.
   *
   * A catalogue total of zero is impossible in a real answer. Raising it as an
   * error puts the retry button in front of the reader instead.
   */
  /*
   * A 200 carrying no `data` at all is a fault too.
   *
   * Reading `.products` off it threw a TypeError, which surfaces as an
   * unhandled rejection rather than the retry the reader needs — the same
   * shape of problem as reporting a fault as an empty result, one level
   * cruder.
   */
  if (!Array.isArray(live.data?.products)) {
    throw new ApiError(
      'bad_response',
      'The rankings are temporarily unavailable. Please retry in a moment.',
      502,
    );
  }

  if (live.data.products.length === 0 && (live.data.catalogTotal ?? 0) === 0) {
    throw new ApiError(
      'server_busy',
      'The rankings are temporarily unavailable. Please retry in a moment.',
      503,
    );
  }

  return live;
}

/**
 * A category filter or a second page, answered from the deep baked file.
 *
 * Returns null whenever the file cannot answer honestly — a narrow category
 * whose products mostly sit below the baked depth, or a page past the end of
 * it. The API then answers, and pays the scan.
 */
async function sliceFromStatic(
  board: string,
  audience: string,
  category: string | null,
  offset: number,
  limit: number,
  signal?: AbortSignal,
): Promise<ApiResult<LeaderboardPayload> | null> {
  const deep = await readStaticLeaderboard(board, audience, undefined, signal, true);
  const source = deep ?? (await readStaticLeaderboard(board, audience, undefined, signal));
  if (source === null) return null;

  const slice = sliceStatic(source.data as unknown as StaticBoardData, {
    category,
    offset,
    limit,
  });
  if (slice === null) return null;

  return {
    data: {
      ...source.data,
      products: slice.products,
      eligible: slice.eligible,
      category,
    },
    meta: { ...source.meta, static: true },
    fromCache: true,
  };
}

/**
 * How deep a per-category baked file goes. Mirrors CATEGORY_DEPTH on the server.
 */
const CATEGORY_DEPTH = 60;

/**
 * A category filter, answered from the file baked FOR that category.
 *
 * Unlike `sliceFromStatic`, which filters the catalogue-wide top 240 and so
 * cannot see a narrow category's winners, this reads a file whose products are
 * already that category's own ranking. It returns null the same way — when the
 * file has run out but the catalogue has not — so a category with more than
 * sixty rankable products still falls through to the API for its deeper pages.
 */
async function sliceFromCategoryFile(
  board: string,
  audience: string,
  category: string,
  offset: number,
  limit: number,
  signal?: AbortSignal,
): Promise<ApiResult<LeaderboardPayload> | null> {
  try {
    const name =
      `${encodeURIComponent(board)}__${encodeURIComponent(audience)}` +
      `__${encodeURIComponent(category)}.json`;
    const response = await fetch(`/data/leaderboard/cat/${name}`, signal ? { signal } : {});
    if (!response.ok) return null;
    // A miss serves the SPA shell as 200 text/html; only JSON is a real file.
    if (!(response.headers.get('content-type') ?? '').includes('json')) return null;

    const body = (await response.json()) as {
      ok?: boolean;
      data?: LeaderboardPayload;
      meta?: ApiMeta;
    };
    if (body.ok !== true || body.data === undefined || body.meta === undefined) return null;
    if (!Array.isArray(body.data.products) || body.data.products.length === 0) return null;

    /*
     * The file's products are ALREADY this category's ranking, so slice with no
     * further category filter. `eligible` in the file is the category's own
     * total, which is exactly the exhaustion test `sliceStatic` needs.
     */
    const slice = sliceStatic(body.data as unknown as StaticBoardData, {
      category: null,
      offset,
      limit,
    });
    if (slice === null) return null;

    return {
      data: { ...body.data, products: slice.products, eligible: slice.eligible, category },
      meta: { ...body.meta, static: true },
      fromCache: true,
    };
  } catch {
    // A failed fetch or parse is not an error here — the API answers instead.
    return null;
  }
}

/**
 * Build a combined ranking from the baked files, or null if they are not all
 * available.
 *
 * Fetched in parallel and served from the service worker after the first
 * visit, so in the common case this touches the network not at all.
 */
async function combineLocally(
  boardIds: string[],
  audience: string,
  limit: number,
  signal?: AbortSignal,
): Promise<ApiResult<LeaderboardPayload> | null> {
  const loaded = await Promise.all(
    boardIds.map((id) => readStaticLeaderboard(id, audience, undefined, signal)),
  );
  // All or nothing: combining three of four chosen criteria would silently
  // answer a different question from the one that was asked.
  if (loaded.some((r) => r === null)) return null;

  const byBoard = new Map<string, Product[]>();
  boardIds.forEach((id, i) => {
    byBoard.set(id, loaded[i]!.data.products);
  });
  if (!canCombineLocally(byBoard, boardIds)) return null;

  const combined = combineBoards(byBoard, boardIds, limit);
  if (combined.products.length === 0) return null;

  const lead = loaded[0]!;
  return {
    data: {
      ...lead.data,
      products: combined.products,
      // The full board definitions, so the page renders the same way whether
      // this came from here or from the server.
      selected: boardIds
        .map((id) => BOARDS.find((b) => b.id === id))
        .filter((b): b is (typeof BOARDS)[number] => b !== undefined),
      combined: combined.entries,
      eligible: combined.eligible,
    },
    meta: { ...lead.meta, static: true },
    fromCache: true,
  };
}

/** How deep the baked rankings go. Mirrors PAYLOAD_DEPTH on the server. */
const STATIC_DEPTH = 240;

/** How many the small default file carries. Mirrors SHALLOW in the build. */
const SHALLOW_DEPTH = 60;

/**
 * Read a baked ranking, or null when there is not one that answers the request.
 *
 * Deliberately silent on failure: a missing file is not an error, it is a
 * request the API should handle. Anything else — a truncated file, a build
 * that wrote nonsense — must also fall through rather than render, because a
 * broken static payload showing zero products would recreate exactly the bug
 * this replaces.
 */
async function readStaticLeaderboard(
  board: string,
  audience: string,
  limit: number | undefined,
  signal?: AbortSignal,
  /** Ask for the 240-deep file rather than the small default one. */
  deep = false,
): Promise<ApiResult<LeaderboardPayload> | null> {
  try {
    // A request for more than the small file holds must read the deep one, or
    // it would be answered short without anyone noticing.
    const wantDeep = deep || (limit !== undefined && limit > SHALLOW_DEPTH);
    const name =
      `${encodeURIComponent(board)}__${encodeURIComponent(audience)}` +
      (wantDeep ? '.deep.json' : '.json');
    const response = await fetch(`/data/leaderboard/${name}`, signal ? { signal } : {});
    if (!response.ok) return null;
    /*
     * Pages serves the SPA shell for any path it does not recognise, so a
     * missing payload arrives as 200 text/html rather than 404. Checking the
     * type first avoids parsing a whole HTML document to discover that.
     */
    if (!(response.headers.get('content-type') ?? '').includes('json')) return null;

    const body = (await response.json()) as {
      ok?: boolean;
      data?: LeaderboardPayload;
      meta?: ApiMeta;
    };
    if (body.ok !== true || body.data === undefined || body.meta === undefined) return null;
    if (!Array.isArray(body.data.products) || body.data.products.length === 0) return null;

    const products =
      limit !== undefined && body.data.products.length > limit
        ? body.data.products.slice(0, limit)
        : body.data.products;

    /*
     * Short is only acceptable when the board really has no more.
     *
     * Otherwise this file is answering a narrower question than the one asked,
     * and the page would show a partial ranking as if it were the whole one.
     */
    if (
      limit !== undefined &&
      products.length < limit &&
      products.length < (body.data.eligible ?? 0)
    ) {
      return null;
    }

    return {
      data: { ...body.data, products },
      meta: body.meta,
      fromCache: true,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Per-product catalog ranking
// ---------------------------------------------------------------------------

export interface BoardRank {
  boardId: string;
  label: string;
  formula: string;
  /** 1-based position across the whole catalog, or null when not rankable. */
  rank: number | null;
  /** How many products were eligible for this board. */
  eligible: number;
  /** Top N% — only meaningful when `rank` is present. */
  percentile: number | null;
  /** Present exactly when `rank` is null. */
  unavailableReason: string | null;
}

export interface RankPayload {
  productId: string;
  ranks: BoardRank[];
  catalogTotal: number;
}

export function getProductRanks(
  id: string,
  signal?: AbortSignal,
): Promise<ApiResult<RankPayload>> {
  return request<RankPayload>(
    `/api/rank/${encodeURIComponent(id)}`,
    signal ? { signal } : {},
  );
}
